import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

interface GratiDaysFieldProps {
  /** Label for the entire component */
  label?: string;
  /** Value for "opens" in total days (-1 = immediately) */
  opensValue?: number;
  /** Value for "closes" in total days */
  closesValue?: number;
  /** Called when opens value changes */
  onOpensChange: (value: number | undefined) => void;
  /** Called when closes value changes */
  onClosesChange: (value: number | undefined) => void;
  /** Placeholder for opens (from parent) */
  opensPlaceholder?: string;
  /** Placeholder for closes (from parent) */
  closesPlaceholder?: string;
  /** Whether values are inherited (show as placeholders) */
  showPlaceholders?: boolean;
}

interface WeeksDays {
  weeks: string;
  days: string;
}

function totalDaysToWeeksDays(totalDays: number | undefined): WeeksDays {
  if (totalDays === undefined || totalDays < 0) {
    return { weeks: '', days: '' };
  }
  return {
    weeks: String(Math.floor(totalDays / 7)),
    days: String(totalDays % 7),
  };
}

function weeksDaysToTotalDays(weeks: string, days: string): number | undefined {
  const w = weeks === '' ? 0 : parseInt(weeks);
  const d = days === '' ? 0 : parseInt(days);
  if (isNaN(w) || isNaN(d)) return undefined;
  // If both are empty strings (meaning user cleared them), return undefined
  if (weeks === '' && days === '') return undefined;
  return w * 7 + d;
}

export default function GratiDaysField(props: GratiDaysFieldProps): ReactElement {
  const {
    label = 'Signup time before event',
    opensValue,
    closesValue,
    onOpensChange,
    onClosesChange,
    opensPlaceholder,
    closesPlaceholder,
    showPlaceholders = false,
  } = props;

  const isImmediately = opensValue === -1;

  // Local state for input values (strings to preserve user input)
  const [opensLocal, setOpensLocal] = useState<WeeksDays>(() => totalDaysToWeeksDays(opensValue));
  const [closesLocal, setClosesLocal] = useState<WeeksDays>(() => totalDaysToWeeksDays(closesValue));

  // Sync local state when prop values change (e.g., from external updates)
  useEffect(() => {
    const newWeeksDays = totalDaysToWeeksDays(opensValue);
    setOpensLocal((prev) => {
      // Only update if the computed total is different, to preserve user typing
      const prevTotal = weeksDaysToTotalDays(prev.weeks, prev.days);
      if (prevTotal !== opensValue) {
        return newWeeksDays;
      }
      return prev;
    });
  }, [opensValue]);

  useEffect(() => {
    const newWeeksDays = totalDaysToWeeksDays(closesValue);
    setClosesLocal((prev) => {
      const prevTotal = weeksDaysToTotalDays(prev.weeks, prev.days);
      if (prevTotal !== closesValue) {
        return newWeeksDays;
      }
      return prev;
    });
  }, [closesValue]);

  // Parse placeholder values for display
  const opensPlaceholderWeeksDays = useMemo(() => {
    if (!opensPlaceholder) return null;
    const num = parseInt(opensPlaceholder);
    if (isNaN(num) || num < 0) return null;
    const wd = totalDaysToWeeksDays(num);
    return { weeks: wd.weeks || '0', days: wd.days || '0' };
  }, [opensPlaceholder]);

  const closesPlaceholderWeeksDays = useMemo(() => {
    if (!closesPlaceholder) return null;
    const num = parseInt(closesPlaceholder);
    if (isNaN(num) || num < 0) return null;
    const wd = totalDaysToWeeksDays(num);
    return { weeks: wd.weeks || '0', days: wd.days || '0' };
  }, [closesPlaceholder]);

  const handleOpensWeeksChange = useCallback((value: string) => {
    setOpensLocal((prev) => ({ ...prev, weeks: value }));
  }, []);

  const handleOpensDaysChange = useCallback((value: string) => {
    // Clamp to 0-6 on input
    const num = parseInt(value);
    if (value !== '' && !isNaN(num)) {
      const clamped = Math.max(0, Math.min(6, num));
      setOpensLocal((prev) => ({ ...prev, days: String(clamped) }));
    } else {
      setOpensLocal((prev) => ({ ...prev, days: value }));
    }
  }, []);

  const handleOpensBlur = useCallback(() => {
    const newTotal = weeksDaysToTotalDays(opensLocal.weeks, opensLocal.days);
    if (newTotal !== undefined) {
      onOpensChange(newTotal);
      // Ensure closes is less than opens
      if (closesValue !== undefined && closesValue >= newTotal) {
        onClosesChange(Math.max(0, newTotal - 1));
      }
    } else {
      onOpensChange(undefined);
    }
  }, [opensLocal, closesValue, onOpensChange, onClosesChange]);

  const handleClosesWeeksChange = useCallback((value: string) => {
    setClosesLocal((prev) => ({ ...prev, weeks: value }));
  }, []);

  const handleClosesDaysChange = useCallback((value: string) => {
    // Clamp to 0-6 on input
    const num = parseInt(value);
    if (value !== '' && !isNaN(num)) {
      const clamped = Math.max(0, Math.min(6, num));
      setClosesLocal((prev) => ({ ...prev, days: String(clamped) }));
    } else {
      setClosesLocal((prev) => ({ ...prev, days: value }));
    }
  }, []);

  const handleClosesBlur = useCallback(() => {
    let newTotal = weeksDaysToTotalDays(closesLocal.weeks, closesLocal.days);
    if (newTotal !== undefined) {
      // Ensure closes is less than opens
      const maxTotal = opensValue !== undefined && opensValue !== -1 ? opensValue - 1 : 999;
      newTotal = Math.min(newTotal, maxTotal);
      onClosesChange(newTotal);
      // Update local state if clamped
      setClosesLocal(totalDaysToWeeksDays(newTotal));
    } else {
      onClosesChange(undefined);
    }
  }, [closesLocal, opensValue, onClosesChange]);

  const handleImmediatelyChange = useCallback(
    (checked: boolean) => {
      if (checked) {
        onOpensChange(-1);
      } else {
        // Default to 1 week when unchecking
        onOpensChange(7);
        setOpensLocal({ weeks: '1', days: '0' });
      }
    },
    [onOpensChange],
  );

  return (
    <Stack spacing={0.5}>
      {label && (
        <Typography variant="labelMedium" sx={{
          color: "text.secondary"
        }}>
          {label}
        </Typography>
      )}
      <Stack direction="row" spacing={3} sx={{
        alignItems: "flex-start"
      }}>
        {/* Opens column */}
        <Stack spacing={0.5}>
          <Typography variant="body2" sx={{
            color: "text.secondary"
          }}>
            Opens
          </Typography>
          {isImmediately ? (
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', fontStyle: 'italic', height: '2em', display: 'flex', alignItems: 'center' }}
            >
              When created.
            </Typography>
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <TextField
                id="opens-weeks"
                aria-label="Opens weeks before event"
                type="number"
                size="small"
                value={opensLocal.weeks}
                placeholder={showPlaceholders && opensPlaceholderWeeksDays ? opensPlaceholderWeeksDays.weeks : undefined}
                onChange={(e) => handleOpensWeeksChange(e.target.value)}
                onBlur={handleOpensBlur}
                slotProps={{
                  htmlInput: { min: 0, style: { textAlign: 'right' } },
                  input: {
                    endAdornment: <InputAdornment position="end">w</InputAdornment>,
                  },
                }}
                sx={{ width: '5em' }}
              />
              <TextField
                id="opens-days"
                aria-label="Opens days before event"
                type="number"
                size="small"
                value={opensLocal.days}
                placeholder={showPlaceholders && opensPlaceholderWeeksDays ? opensPlaceholderWeeksDays.days : undefined}
                onChange={(e) => handleOpensDaysChange(e.target.value)}
                onBlur={handleOpensBlur}
                slotProps={{
                  htmlInput: { min: 0, max: 6, style: { textAlign: 'right' } },
                  input: {
                    endAdornment: <InputAdornment position="end">d</InputAdornment>,
                  },
                }}
                sx={{ width: '4.5em' }}
              />
            </Box>
          )}
          <FormControlLabel
            control={
              <Checkbox
                checked={isImmediately}
                onChange={(e) => handleImmediatelyChange(e.target.checked)}
                size="small"
                sx={{ py: 0 }}
              />
            }
            label="Immediately"
            slotProps={{ typography: { variant: 'body2' } }}
            sx={{ ml: -0.5 }}
          />
        </Stack>

        {/* Closes column */}
        <Stack spacing={0.5}>
          <Typography variant="body2" sx={{
            color: "text.secondary"
          }}>
            Closes
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <TextField
              id="closes-weeks"
              aria-label="Closes weeks before event"
              type="number"
              size="small"
              value={closesLocal.weeks}
              placeholder={showPlaceholders && closesPlaceholderWeeksDays ? closesPlaceholderWeeksDays.weeks : undefined}
              onChange={(e) => handleClosesWeeksChange(e.target.value)}
              onBlur={handleClosesBlur}
              slotProps={{
                htmlInput: { min: 0, style: { textAlign: 'right' } },
                input: {
                  endAdornment: <InputAdornment position="end">w</InputAdornment>,
                },
              }}
              sx={{ width: '5em' }}
            />
            <TextField
              id="closes-days"
              aria-label="Closes days before event"
              type="number"
              size="small"
              value={closesLocal.days}
              placeholder={showPlaceholders && closesPlaceholderWeeksDays ? closesPlaceholderWeeksDays.days : undefined}
              onChange={(e) => handleClosesDaysChange(e.target.value)}
              onBlur={handleClosesBlur}
              slotProps={{
                htmlInput: { min: 0, max: 6, style: { textAlign: 'right' } },
                input: {
                  endAdornment: <InputAdornment position="end">d</InputAdornment>,
                },
              }}
              sx={{ width: '4.5em' }}
            />
          </Box>
        </Stack>
      </Stack>
    </Stack>
  );
}
